import React from 'react';
import { Card, Badge, Table } from 'flowbite-react';
import { HiCheckCircle, HiXCircle, HiDocumentText, HiChartBar } from 'react-icons/hi';
import type { ComplianceRecord, ComplianceSummary } from '../../api/workflowInspector';

interface CompliancePanelProps {
  standards: ComplianceRecord[];
  summary: ComplianceSummary;
}

const CompliancePanel: React.FC<CompliancePanelProps> = ({ standards, summary }) => {
  if (standards.length === 0) {
    return (
      <Card>
        <h4 className="text-md font-semibold text-gray-900 mb-2">Standards Compliance</h4>
        <p className="text-sm text-gray-500">No standards were delivered for this execution.</p>
      </Card>
    );
  }

  const compliancePercent = summary.Compliance_Rate;

  return (
    <Card>
      <h4 className="text-md font-semibold text-gray-900 mb-4">Standards Compliance</h4>

      {/* Summary metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="flex items-center gap-2">
          <HiDocumentText className="w-5 h-5 text-blue-500" />
          <div>
            <p className="text-xs text-gray-500">Delivered</p>
            <p className="text-sm font-medium">{summary.Total_Delivered}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <HiCheckCircle className="w-5 h-5 text-green-500" />
          <div>
            <p className="text-xs text-gray-500">Followed</p>
            <p className="text-sm font-medium">{summary.Followed}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <HiXCircle className="w-5 h-5 text-red-500" />
          <div>
            <p className="text-xs text-gray-500">Violated</p>
            <p className="text-sm font-medium">{summary.Violated}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <HiChartBar className="w-5 h-5 text-indigo-500" />
          <div>
            <p className="text-xs text-gray-500">Compliance</p>
            <p className="text-sm font-medium">{compliancePercent}%</p>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
        <div
          className={`h-2 rounded-full ${
            compliancePercent >= 90 ? 'bg-green-500' :
            compliancePercent >= 70 ? 'bg-yellow-400' : 'bg-red-500'
          }`}
          style={{ width: `${Math.min(compliancePercent, 100)}%` }}
        />
      </div>

      {/* Standards table */}
      <div className="overflow-x-auto">
        <Table hoverable>
          <Table.Head>
            <Table.HeadCell>Standard</Table.HeadCell>
            <Table.HeadCell>Category</Table.HeadCell>
            <Table.HeadCell>Priority</Table.HeadCell>
            <Table.HeadCell>Status</Table.HeadCell>
            <Table.HeadCell>Details</Table.HeadCell>
          </Table.Head>
          <Table.Body className="divide-y">
            {standards.map((s) => (
              <Table.Row key={s.ID} className="bg-white">
                <Table.Cell className="font-medium text-gray-900 text-sm">
                  {s.Standard_Title}
                </Table.Cell>
                <Table.Cell className="text-sm text-gray-500">
                  {s.Standard_Category || '-'}
                </Table.Cell>
                <Table.Cell className="text-sm">
                  {s.Standard_Priority != null ? (
                    <Badge color={s.Standard_Priority <= 2 ? 'failure' : s.Standard_Priority <= 5 ? 'warning' : 'gray'} size="xs">
                      P{s.Standard_Priority}
                    </Badge>
                  ) : '-'}
                </Table.Cell>
                <Table.Cell>
                  {s.Violated ? (
                    <Badge color="failure" size="xs">Violated</Badge>
                  ) : s.Followed ? (
                    <Badge color="success" size="xs">Followed</Badge>
                  ) : (
                    <Badge color="gray" size="xs">Pending</Badge>
                  )}
                </Table.Cell>
                <Table.Cell className="text-xs text-gray-500 max-w-xs truncate">
                  {s.Violated && s.Violation_Details?.reason ? s.Violation_Details.reason : '-'}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    </Card>
  );
};

export default CompliancePanel;
